import _extends from '@babel/runtime/helpers/esm/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import { useState, useRef, useMemo, useEffect } from 'react';
import { css, useTheme, jsx, ClassNames } from '@emotion/react';
import { ChevronUpIcon, ChevronDownIcon } from '@paypalcorp/pp-react-icons';
import { TextInput } from '@paypalcorp/pp-react-text-input';
import { SelectionMenu } from '@paypalcorp/pp-react-menu';
import _taggedTemplateLiteral from '@babel/runtime/helpers/esm/taggedTemplateLiteral';
import { uniqueId, KeyboardCode } from '@paypalcorp/pp-react-utils';

var _templateObject, _templateObject2, _templateObject3;

function styles(tokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    console.warn('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }

  var sysSizeFieldLg = tokens.sysSizeFieldLg;
  var base = css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    position: relative;\n  "])));
  var menu = css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    top: ", ";\n  "])), sysSizeFieldLg);
  var affordanceTrigger = css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n    label: affordance_trigger;\n    cursor: pointer;\n  "])));
  return {
    base: base,
    menu: menu,
    affordanceTrigger: affordanceTrigger
  };
}

var _excluded = ["name", "options", "value", "text", "id", "dir", "label", "onOpen", "onChange", "onBlur", "onTextChange", "onFocus", "getMenuOptions", "placeholder", "isLoadingOptions", "emptyMessage", "mobileTitle", "sheetWrapperClassName", "autoComplete"];

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
} // By default, matching is case-insensitive prefix match


var defaultGetMenuOptions = function defaultGetMenuOptions(optionList, userText) {
  if (!userText) {
    // No filter means you get it all, no questions asked!
    return optionList;
  }

  return optionList.filter(function (option) {
    // istanbul ignore next
    var comparand = (option.label || '').toLowerCase();
    var prefix = userText.toLowerCase();
    return comparand.substring(0, prefix.length) === prefix;
  });
};

var getOptionValue = function getOptionValue(option) {
  var _option$value;

  return (_option$value = option.value) !== null && _option$value !== void 0 ? _option$value : option.label;
}; // Make sure all items have an id we can use for aria tags


var ideate = function ideate(optionList, name) {
  // istanbul ignore next (this function is called, but converage doesn't see it
  return optionList.reduce(function (result, option) {
    var mappedOption = _objectSpread({}, option);

    if (!mappedOption.hasOwnProperty('id')) {
      mappedOption.id = "".concat(name, "__").concat(getOptionValue(option));
    }

    result.push(mappedOption);
    return result;
  }, []);
};

var defaultEmptyMessage = 'No results. Try something else or check for errors.';
var optionLegend = {
  label: 'primaryText'
};

var mapObj = function mapObj(src, legend) {
  return Object.entries(src).reduce(function (dest, _ref) {
    var _ref2 = _slicedToArray(_ref, 2),
        key = _ref2[0],
        value = _ref2[1];

    var newKey = legend.hasOwnProperty(key) ? legend[key] : key;
    dest[newKey] = value;
    return dest;
  }, {});
}; // istanbul ignore next


var mapOptions = function mapOptions(srcList) {
  return srcList ? srcList.map(function (option) {
    // add onMouseDown to prevent onBlur being called when selecting an option
    option.onMouseDown = function (evt) {
      return evt.preventDefault();
    };

    return mapObj(option, optionLegend);
  }) : [];
}; // eslint-disable-next-line max-statements


var Combobox = function Combobox(_ref3) {
  var _ref3$name = _ref3.name,
      name = _ref3$name === void 0 ? 'NO_NAME' : _ref3$name,
      options = _ref3.options,
      _ref3$value = _ref3.value,
      value = _ref3$value === void 0 ? '' : _ref3$value,
      text = _ref3.text,
      id = _ref3.id,
      dir = _ref3.dir,
      label = _ref3.label,
      onOpen = _ref3.onOpen,
      onChange = _ref3.onChange,
      onBlur = _ref3.onBlur,
      onTextChange = _ref3.onTextChange,
      _ref3$onFocus = _ref3.onFocus,
      onFocus = _ref3$onFocus === void 0 ? null : _ref3$onFocus,
      _ref3$getMenuOptions = _ref3.getMenuOptions,
      getMenuOptions = _ref3$getMenuOptions === void 0 ? defaultGetMenuOptions : _ref3$getMenuOptions,
      placeholder = _ref3.placeholder,
      isLoadingOptions = _ref3.isLoadingOptions,
      _ref3$emptyMessage = _ref3.emptyMessage,
      emptyMessage = _ref3$emptyMessage === void 0 ? defaultEmptyMessage : _ref3$emptyMessage,
      _ref3$mobileTitle = _ref3.mobileTitle,
      mobileTitle = _ref3$mobileTitle === void 0 ? label : _ref3$mobileTitle,
      sheetWrapperClassName = _ref3.sheetWrapperClassName,
      autoComplete = _ref3.autoComplete,
      rest = _objectWithoutProperties(_ref3, _excluded);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      showMenu = _useState2[0],
      setShowMenu = _useState2[1];

  var _useState3 = useState(-1),
      _useState4 = _slicedToArray(_useState3, 2),
      activeIndex = _useState4[0],
      setActiveIndex = _useState4[1];

  var _useState5 = useState(''),
      _useState6 = _slicedToArray(_useState5, 2),
      placemat = _useState6[0],
      setPlacemat = _useState6[1];

  var _useState7 = useState(options),
      _useState8 = _slicedToArray(_useState7, 2),
      filteredList = _useState8[0],
      setFilteredList = _useState8[1];

  var _useState9 = useState(value),
      _useState10 = _slicedToArray(_useState9, 2),
      activeItem = _useState10[0],
      setActiveItem = _useState10[1];

  var _useState11 = useState(false),
      _useState12 = _slicedToArray(_useState11, 2),
      textFocus = _useState12[0],
      setTextFocus = _useState12[1];

  var sheetInputRef = useRef();
  var textInputRef = useRef();

  var getInitialText = function getInitialText(selectedValue, currentText) {
    if (currentText !== '' && (!selectedValue || !options)) {
      return currentText;
    }

    var selectedOption = options === null || options === void 0 ? void 0 : options.find(function (option) {
      return selectedValue === getOptionValue(option);
    });
    return selectedOption ? selectedOption.label : selectedValue;
  };

  var initialText = getInitialText(value, text);

  var _useState13 = useState(initialText || ''),
      _useState14 = _slicedToArray(_useState13, 2),
      textValue = _useState14[0],
      setTextValue = _useState14[1];

  var _useState15 = useState(''),
      _useState16 = _slicedToArray(_useState15, 2),
      sheetTextValue = _useState16[0],
      setSheetTextValue = _useState16[1];

  var comboId = id !== null && id !== void 0 ? id : uniqueId('Combobox_');
  var textInputId = "combo_txt_".concat(comboId);
  var menuId = "combo_menu_".concat(comboId);

  var clearActiveItem = function clearActiveItem() {
    setActiveIndex(-1);
    setPlacemat('');
  };

  var updateFilteredList = function updateFilteredList(filterText) {
    var updatedList = ideate(getMenuOptions(options, filterText), name);

    if (!updatedList || updatedList.length === 0) {
      updatedList = [{
        label: emptyMessage,
        unselectable: true
      }];
    }

    setFilteredList(updatedList);
    clearActiveItem();
  }; // Spoof an event so the caller thinks a change happened in this component


  var changeCallback = function changeCallback(newValue) {
    // istanbul ignore next (sanity check)
    if (typeof onChange === 'function') {
      onChange({
        target: {
          name: name,
          value: newValue
        }
      });
    }
  };

  var doSetShowMenu = function doSetShowMenu(isShown) {
    setShowMenu(isShown);
    setTextFocus(isShown);
  };

  var openMenu = function openMenu() {
    if (!isLoadingOptions) {
      updateFilteredList('');
      setSheetTextValue('');
    }

    doSetShowMenu(true); // istanbul ignore next

    if (textInputRef.current) {
      textInputRef.current.select();
    }
  };

  var handleSheetTyping = function handleSheetTyping(evt) {
    // istanbul ignore next
    if (typeof onTextChange === 'function') {
      onTextChange(evt);
    }

    setSheetTextValue(evt.target.value);
    setTextValue(evt.target.value);
    updateFilteredList(evt.target.value);
    setActiveItem('');
  };

  var handleClose = function handleClose() {
    doSetShowMenu(false);
    setPlacemat('');
  }; // Clicks inside the text input are NOT outside clicks!


  var outsideClickCheck = function outsideClickCheck(evt) {
    var target = evt.target;
    return target.id !== textInputId;
  };

  var handleItemSelection = function handleItemSelection(index) {
    var selectedOption = filteredList[index]; // istanbul ignore next

    if (selectedOption && !selectedOption.unselectable) {
      setTextValue(selectedOption.label);
      setActiveItem(getOptionValue(selectedOption));
      setSheetTextValue(selectedOption.label);
      changeCallback(getOptionValue(selectedOption));
      doSetShowMenu(false);
    }
  }; // This is a simplistic way of telling. We may need to use a state... --BCC
  // istanbul ignore next


  var isFiltered = function isFiltered() {
    return textValue === '' || filteredList.length < options.length;
  };

  var getActiveId = function getActiveId() {
    return activeIndex < 0 ? '' : filteredList[activeIndex].id;
  };

  var updateActiveItem = function updateActiveItem(offset) {
    var nextIndex = Math.min(Math.max(activeIndex + offset, 0), filteredList.length - 1);

    if (nextIndex !== activeIndex) {
      var selectedOption = filteredList[nextIndex];

      if (!selectedOption.unselectable) {
        setActiveIndex(nextIndex); // We can have text in the text input that's not actually filtering the list yet.
        // (if we reopen after a selection has been made) This can lead to mismatches
        // between the text and placemat. So if we're not filtering, no placemat for you!
        // --BCC
        // istanbul ignore next

        if (selectedOption && isFiltered()) {
          setPlacemat(selectedOption.label);
        }
      }
    }
  }; // eslint-disable-next-line complexity


  var handleKey = function handleKey(evt) {
    /* istanbul ignore next */
    var key = evt.key || evt.keyCode;
    /* istanbul ignore next */

    var KeyCode = new KeyboardCode(key);

    if (KeyCode.IS_ARROW_UP) {
      updateActiveItem(-1);
    } else if (KeyCode.IS_ARROW_DOWN) {
      if (!showMenu) {
        openMenu();
      } else {
        updateActiveItem(1);
      }
    } else if (KeyCode.IS_ENTER || key === 'Unidentified') {
      // HACK ALERT! In test environment `jest` sends `enter` key as `Unidentified`
      evt.preventDefault();

      if (!showMenu) {
        openMenu();
      } else {
        handleItemSelection(activeIndex);
      }
    } else if (KeyCode.IS_TAB || KeyCode.IS_ESC) {
      handleClose();
    }
  };

  var sheetTextPlaceholder = placemat ? null : placeholder;
  var sheetTextInputName = "sheet_t_".concat(name);

  var getSheetTextInput = function getSheetTextInput() {
    var inputDir = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'ltr';
    return jsx("div", {
      dir: inputDir,
      "data-ppui": "true"
    }, jsx(TextInput, {
      name: sheetTextInputName,
      ref: sheetInputRef,
      label: label,
      value: sheetTextValue,
      placeholder: sheetTextPlaceholder,
      placemat: placemat,
      onChange: handleSheetTyping,
      onKeyDown: function onKeyDown(evt) {
        return handleKey(evt);
      },
      autoComplete: "off",
      role: "combobox",
      "aria-controls": menuId,
      "aria-activedescendant": getActiveId(),
      disabled: isLoadingOptions,
      "data-ppui": "true"
    }));
  };

  var tokens = useTheme();
  var comboStyles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);
  var AffordanceIcon = showMenu ? ChevronUpIcon : ChevronDownIcon;
  var affordance = jsx(AffordanceIcon, {
    onClick: function toggleMenu(evt) {
      evt.stopPropagation();

      if (showMenu) {
        doSetShowMenu(false);
      } else {
        setTextFocus(true);
        openMenu();
      }
    },
    size: "sm",
    "aria-hidden": "true",
    "data-testid": "combo-affordance",
    color: "BackgroundMediumContrast",
    css: comboStyles.affordanceTrigger,
    "data-ppui": "true"
  });
  useEffect(function () {
    setActiveItem(value);
    setTextValue(getInitialText(value, ''));
  }, [value]);
  useEffect(function () {
    // Set the text only if it was explicitly passed in
    if (text !== undefined && text !== null) {
      setTextValue(text);
    }
  }, [text]);

  var getRefCurrent = function getRefCurrent(ref) {
    return (
      /* istanbul ignore next */
      ref && ref.current ? ref.current : null
    );
  };

  useEffect(function () {
    if (!isLoadingOptions && showMenu) {
      var sheetInput = getRefCurrent(sheetInputRef);
      /* istanbul ignore next */

      if (sheetInput) {
        sheetInput.focus();
      }
    } else if (options && options.length) {
      setFilteredList(options);
      setTextValue(getInitialText(value, textValue));
    }
  }, [isLoadingOptions, options]);
  var inputProps = {
    role: 'combobox',
    autoComplete: autoComplete !== null && autoComplete !== void 0 ? autoComplete : 'new-password',
    'aria-expanded': showMenu,
    'aria-haspopup': true,
    'aria-controls': menuId,
    'aria-activedescendant': getActiveId(),
    value: textValue,
    placeholder: placemat ? ' ' : placeholder,
    placemat: placemat,
    inputRef: textInputRef
  };
  var menuProps = {
    isLoadingOptions: isLoadingOptions
  }; // aria-owns={menuId} This will be needed if SelectionMenu goes into a portal

  return jsx("div", {
    dir: dir,
    css: comboStyles.base,
    "data-ppui-info": "combobox_3.5.12"
  }, jsx(TextInput, _extends({
    name: "combo_t_".concat(name),
    label: label,
    id: textInputId,
    rightIcon: affordance,
    setFocus: textFocus
  }, rest, inputProps, {
    onChange: function handleChange(evt) {
      if (typeof onTextChange === 'function') {
        onTextChange(evt);
      }

      setTextValue(evt.target.value);
      updateFilteredList(evt.target.value);
      setActiveItem('');

      if (!showMenu) {
        doSetShowMenu(true);
      }
    },
    onClick: function handleFocus() {
      if (!showMenu) {
        openMenu();
      }
    },
    onKeyDown: handleKey,
    onFocus: function openOnFocus(evt) {
      var sheetInput = getRefCurrent(sheetInputRef);

      if (onFocus && !sheetInput) {
        onFocus(evt);
      }
    },
    onBlur: function handleBlur() {
      // Are we transferring focus to a newly opened sheet in mweb?
      if (sheetInputRef.current) {
        return; // Don't call onBlur(). This is a "false" blur!
      }

      if (onBlur) {
        onBlur({
          target: {
            name: name,
            textValue: textValue,
            value: activeItem
          }
        });
      }
    },
    "data-reactcombo": true,
    "data-ppui": "true"
  })), jsx(ClassNames, {
    "data-ppui": "true"
  }, function (_ref4) {
    var css = _ref4.css;
    return jsx(SelectionMenu, _extends({
      name: "combo_m_".concat(name),
      value: activeItem,
      id: menuId
    }, menuProps, {
      initialItemFocus: false,
      ignoreKeyboard: true,
      isOpen: showMenu,
      mobileTitle: mobileTitle,
      mobileHeaderContents: getSheetTextInput(dir),
      outsideClickCheck: outsideClickCheck,
      onOpen: onOpen,
      onClose: handleClose,
      onSelection: handleItemSelection,
      activeItem: activeIndex,
      triggerElementRef: textInputRef,
      options: mapOptions(filteredList),
      containerClassName: css(comboStyles.menu),
      sheetWrapperClassName: sheetWrapperClassName,
      "data-ppui": "true"
    }));
  }));
};

export { Combobox, styles as comboboxStyles };